import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, Observable } from 'rxjs';
import { DriftConfiguration } from '../models/configuration.interface';
import { ConfigurationService } from './configuration.service';

// Interfaces para los modelos
export interface ForwardingCondition {
  condition_id?: string;
  column_name: string;
  operator: 'equals' | 'contains' | 'starts_with';
  value: string;
  logic_operator?: 'and' | 'or' | null;
}

export interface ForwardingRule {
  rule_id: string;
  name: string;
  organization_id: string;
  conditions: ForwardingCondition[];
  target_collection_id: string;
  frequency: 'pastAndFuture' | 'future';
  status: 'active' | 'inactive' | 'paused';
  created_at: string;
  updated_at: string;
  created_by: string;
  last_executed?: string;
  execution_count: number;
}

export interface CreateForwardingRuleRequest {
  name: string;
  conditions: ForwardingCondition[];
  target_collection_id: string;
  frequency: 'pastAndFuture' | 'future';
  organization_id: string;
}

export interface UpdateForwardingRuleRequest {
  name?: string;
  conditions?: ForwardingCondition[];
  target_collection_id?: string;
  frequency?: 'pastAndFuture' | 'future';
  status?: 'active' | 'inactive' | 'paused';
}

export interface TestRuleRequest {
  test_data: Record<string, unknown>;
}

export interface ConditionResult {
  condition_index: number;
  condition: {
    column_name: string;
    operator: string;
    value: string;
  };
  document_value: unknown;
  matches: boolean;
}

export interface TestRuleResponse {
  rule_id: string;
  rule_name: string;
  overall_match: boolean;
  condition_results: ConditionResult[];
  test_data: Record<string, unknown>;
}

export interface ExecuteRuleResponse {
  rule_id: string;
  executed_at: string;
  items_processed: number;
  items_forwarded: number;
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class ForwardingRuleService {
  config: DriftConfiguration;
  baseUrl = '';

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService
  ) {
    this.config = {};

    this.configurationService
      .configurationLoaded()
      .pipe(filter(loaded => loaded === true))
      .subscribe(() => {
        this.config = this.configurationService.getConfiguration();
      });

    this.baseUrl = `${this.config.driftApi}/forwarding-rules`;
  }

  /*
   * GET ALL - Obtener reglas con filtros opcionales
   * Endpoint: GET /forwarding-rules/all
   * @param options - Opciones de filtrado
   *   - organizationId: Filtrar por ID de organización
   *   - currentUser: Filtrar por usuario actual (true/false)
   *   - Sin parámetros: Retorna todas las reglas
   */
  getAllForwardingRules(options?: { organizationId?: string; currentUser?: boolean }): Observable<ForwardingRule[]> {
    const url = `${this.baseUrl}/all`;
    let params = new HttpParams();

    if (options?.organizationId) {
      params = params.set('organization_id', options.organizationId);
    }

    if (options?.currentUser) {
      params = params.set('current_user', 'true');
    }

    return this.http.get<ForwardingRule[]>(url, { params });
  }

  /*
   * GET BY ORGANIZATION - Obtener reglas por organización
   * Endpoint: GET /forwarding-rules/all?organization_id={id}
   */
  getForwardingRulesByOrganization(organizationId: string): Observable<ForwardingRule[]> {
    return this.getAllForwardingRules({ organizationId });
  }

  /*
   * GET CURRENT USER RULES - Obtener reglas del usuario actual
   * Endpoint: GET /forwarding-rules/all?current_user=true
   */
  getCurrentUserForwardingRules(): Observable<ForwardingRule[]> {
    return this.getAllForwardingRules({ currentUser: true });
  }

  /*
   * GET CURRENT USER RULES BY ORGANIZATION - Obtener reglas del usuario actual en una organización
   * Endpoint: GET /forwarding-rules/all?organization_id={id}&current_user=true
   */
  getCurrentUserForwardingRulesByOrganization(organizationId: string): Observable<ForwardingRule[]> {
    return this.getAllForwardingRules({ organizationId, currentUser: true });
  }

  /*
   * GET BY ID - Get a specific rule
   * Endpoint: GET /forwarding-rules/{rule_id}
   */
  getForwardingRuleById(ruleId: string): Observable<ForwardingRule> {
    const url = `${this.baseUrl}/${ruleId}`;
    return this.http.get<ForwardingRule>(url);
  }

  /*
   * POST - Create new rule
   * Endpoint: POST /forwarding-rules
   */
  createForwardingRule(rule: CreateForwardingRuleRequest): Observable<ForwardingRule> {
    return this.http.post<ForwardingRule>(this.baseUrl, rule);
  }

  /**
   * PUT - Actualizar regla existente
   * Endpoint: PUT /forwarding-rules/{rule_id}
   */
  updateForwardingRule(ruleId: string, updates: UpdateForwardingRuleRequest): Observable<ForwardingRule> {
    const url = `${this.baseUrl}/${ruleId}`;
    return this.http.put<ForwardingRule>(url, updates);
  }

  /**
   * DELETE - Eliminar regla (soft delete)
   * Endpoint: DELETE /forwarding-rules/{rule_id}
   */
  deleteForwardingRule(ruleId: string): Observable<{ message: string }> {
    const url = `${this.baseUrl}/${ruleId}`;
    return this.http.delete<{ message: string }>(url);
  }

  /*
   * POST - Probar regla con datos de ejemplo
   * Endpoint: POST /forwarding-rules/{rule_id}/test
   TODO: PENDING TO CHECK
   */
  testForwardingRule(ruleId: string, testData: TestRuleRequest): Observable<TestRuleResponse> {
    const url = `${this.baseUrl}/${ruleId}/test`;
    return this.http.post<TestRuleResponse>(url, testData);
  }

  /*
   * POST - Ejecutar regla manualmente
   * Endpoint: POST /forwarding-rules/{rule_id}/execute
   TODO: PENDING TO CHECK
   */
  executeForwardingRule(ruleId: string): Observable<ExecuteRuleResponse> {
    const url = `${this.baseUrl}/${ruleId}/execute`;
    return this.http.post<ExecuteRuleResponse>(url, {});
  }

  // /**
  //  * GET con manejo de errores específico
  //  */
  // getForwardingRuleByIdWithErrorHandling(ruleId: string): Observable<ForwardingRule> {
  //   const url = `${this.baseUrl}/${ruleId}`;
  //   return this.http
  //     .get<ForwardingRule>(url)
  //     .pipe
  //     // Aquí podrías agregar operators de RxJS para manejo de errores
  //     // catchError, retry, timeout, etc.
  //     ();
  // }
}
